// ActionTodayPanel — bloque destacado: "Casos que requieren acción hoy".
// Muestra hasta 8 casos priorizados con nivel + carril + motivos como chips.
// NO expone el score crudo.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, HelpCircle } from 'lucide-react';
import { nivelInfo, carrilInfo, motivoInfo, ESTADOS, anonLabel } from '../../../lib/dashboardVocab';
import PriorityExplanationModal from './PriorityExplanationModal';

export default function ActionTodayPanel({ casos, loading }) {
  const [explain, setExplain] = useState({ open: false, nivel: 'verde', motivos: [] });
  const visibles = (casos || []).slice(0, 8);
  const total = casos?.length || 0;

  return (
    <section
      aria-label="Casos que requieren acción hoy"
      className="bg-white rounded-2xl border border-[#E5EAF5] overflow-hidden"
      data-testid="action-today-panel"
    >
      <header className="px-5 py-4 border-b border-[#E5EAF5] flex items-center justify-between gap-3">
        <div>
          <h2 className="font-extrabold text-[#0B1B5E]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Requieren acción hoy
          </h2>
          <p className="text-xs text-[#475472] mt-0.5">
            Casos priorizados para revisión humana durante la jornada.
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold text-[#5346E6]" data-testid="action-today-total">
            {loading ? '…' : total}
          </div>
          <div className="text-[10px] text-[#475472] uppercase tracking-wide">Total priorizados</div>
        </div>
      </header>

      {loading ? (
        <div className="p-6 text-sm text-[#475472]" data-testid="action-today-loading">Cargando…</div>
      ) : visibles.length === 0 ? (
        <div className="p-8 text-center" data-testid="action-today-empty">
          <p className="text-sm text-[#475472]">Sin casos que requieran acción hoy.</p>
          <p className="text-xs text-[#94A3B8] mt-1">Buen momento para revisar cursos foco o cerrar seguimientos.</p>
        </div>
      ) : (
        <ul className="divide-y divide-[#F1F3F9]" data-testid="action-today-list">
          {visibles.map((c) => {
            const nivel = nivelInfo(c.nivel_alerta);
            const carril = carrilInfo(c.carril);
            return (
              <li
                key={c.id}
                className="px-5 py-3 hover:bg-[#F9FAFF] transition flex flex-col md:flex-row md:items-center gap-3"
                data-testid={`action-today-item-${c.id}`}
              >
                {/* Nivel + carril */}
                <div className="flex items-center gap-2 min-w-0 md:w-56">
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold"
                    style={{ background: nivel.bg, color: nivel.fg }}
                    data-testid={`nivel-${c.nivel_alerta}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: nivel.dot }} />
                    {nivel.label} · {nivel.prioridad}
                  </span>
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold"
                    style={{ background: carril.bg, color: carril.fg }}
                  >
                    {carril.label}
                  </span>
                </div>

                {/* Descripción */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#0B1B5E] truncate" data-testid={`caso-label-${c.id}`}>
                    {anonLabel(c)}
                    <span className="ml-2 text-xs text-[#475472] font-normal">· {c.dominio_code}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(c.motivos || []).map((m) => {
                      const info = motivoInfo(m);
                      return (
                        <span
                          key={m}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ background: info.bg, color: info.fg }}
                          data-testid={`motivo-${m}`}
                        >
                          {info.label}
                        </span>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-[#475472] mt-1">
                    Estado: {ESTADOS[c.estado] || c.estado}
                    {c.responsable_id ? ' · Responsable asignado' : ' · Sin responsable'}
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-[11px] text-[#5346E6] hover:underline flex items-center gap-1"
                    onClick={() => setExplain({ open: true, nivel: c.nivel_alerta, motivos: c.motivos || [] })}
                    data-testid={`btn-por-que-${c.id}`}
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    ¿Por qué este nivel?
                  </button>
                  <Link
                    to={`/admin/dashboard/casos/${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0B1B5E] hover:bg-[#5346E6] text-white text-xs font-bold transition"
                    data-testid={`btn-ver-caso-${c.id}`}
                  >
                    Ver caso
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <PriorityExplanationModal
        open={explain.open}
        onClose={() => setExplain((s) => ({ ...s, open: false }))}
        nivel={explain.nivel}
        motivos={explain.motivos}
      />
    </section>
  );
}
